import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import * as XLSX from 'xlsx';
import {
  Upload, Search, FileSpreadsheet, X, AlertCircle, Loader2, ArrowRight, Sparkles,
  Mic, MicOff, Camera, Image as ImageIcon, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { getDescriptionQuality, COUNTRIES } from './utils';
import { apiGet, apiPost, ApiError } from '../../lib/apiClient';

export default function AnalyzeTab({ user, setUser, setActiveTab }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const fileInputRef = useRef(null);
  const hsHintTimeoutRef = useRef(null);

  // Single product form state
  const [productForm, setProductForm] = useState({
    description: '',
    country_of_origin: '',
    import_country: '',
    current_hs_code: '',
    unit_cost: '',
    annual_volume: ''
  });
  const [singleLoading, setSingleLoading] = useState(false);

  // Import market helper
  const EU_MEMBERS = new Set([
    "Austria", "Belgium", "Bulgaria", "Croatia", "Cyprus", "Czech Republic",
    "Denmark", "Estonia", "Finland", "France", "Germany", "Greece", "Hungary",
    "Ireland", "Italy", "Latvia", "Lithuania", "Luxembourg", "Malta",
    "Netherlands", "Poland", "Portugal", "Romania", "Slovakia", "Slovenia",
    "Spain", "Sweden",
  ]);

  const getMarketInfo = (importCountry) => {
    if (!importCountry || importCountry === 'United States') return { market: 'US', codeLabel: 'HS Code' };
    if (importCountry === 'United Kingdom') return { market: 'UK', codeLabel: 'Commodity Code' };
    if (importCountry === 'European Union' || EU_MEMBERS.has(importCountry)) return { market: 'EU', codeLabel: 'CN Code' };
    return { market: 'OTHER', codeLabel: 'HS Code' };
  };

  const currentMarket = getMarketInfo(productForm.import_country);

  const bulkSectionRef = useRef(null);

  // Scroll to bulk section when deep-linked with ?mode=bulk (relocated after ref decl to avoid TDZ)
  useEffect(() => {
    if (searchParams.get('mode') === 'bulk') {
      const t = setTimeout(() => {
        bulkSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
      return () => clearTimeout(t);
    }
  }, [searchParams]);

  // Bulk upload state
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  // Bulk AI Enhancement state
  // Bulk AI Enhancement state
  const [fileQualityAnalysis, setFileQualityAnalysis] = useState(null);
  const [bulkEnhanceLoading, setBulkEnhanceLoading] = useState(false);
  const [bulkHsHintLoading, setBulkHsHintLoading] = useState(false);


  // Voice & Image Intelligence state
  const [isRecording, setIsRecording] = useState(false);
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // AI Enhancement state (single product)
  const [enhanceLoading, setEnhanceLoading] = useState(false);
  const [hsCodeHint, setHsCodeHint] = useState(null);
  const [hsHintLoading, setHsHintLoading] = useState(false);

  // Currency helper based on destination country
  const getCurrencyForCountry = (country) => {
    const currencyMap = {
      'United States': { code: 'USD', symbol: '$' },
      'United Kingdom': { code: 'GBP', symbol: '£' },
      'Germany': { code: 'EUR', symbol: '€' },
      'France': { code: 'EUR', symbol: '€' },
      'Italy': { code: 'EUR', symbol: '€' },
      'Spain': { code: 'EUR', symbol: '€' },
      'Netherlands': { code: 'EUR', symbol: '€' },
      'Belgium': { code: 'EUR', symbol: '€' },
      'Austria': { code: 'EUR', symbol: '€' },
      'Ireland': { code: 'EUR', symbol: '€' },
      'Portugal': { code: 'EUR', symbol: '€' },
      'Finland': { code: 'EUR', symbol: '€' },
      'Greece': { code: 'EUR', symbol: '€' },
      'Japan': { code: 'JPY', symbol: '¥' },
      'China': { code: 'CNY', symbol: '¥' },
      'Canada': { code: 'CAD', symbol: 'C$' },
      'Australia': { code: 'AUD', symbol: 'A$' },
      'Mexico': { code: 'MXN', symbol: 'MX$' },
      'Brazil': { code: 'BRL', symbol: 'R$' },
      'India': { code: 'INR', symbol: '₹' },
      'South Korea': { code: 'KRW', symbol: '₩' },
      'Switzerland': { code: 'CHF', symbol: 'CHF ' },
      'Singapore': { code: 'SGD', symbol: 'S$' },
      'Hong Kong': { code: 'HKD', symbol: 'HK$' },
    };
    return currencyMap[country] || { code: 'USD', symbol: '$' };
  };

  const getUnitCostCurrency = () => {
    const country = productForm.import_country || 'United States';
    return getCurrencyForCountry(country);
  };


  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await processVoiceRecording(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.info('Recording... Speak your product description');
    } catch (error) {
      toast.error('Microphone access denied. Please allow microphone access.');
      console.error('Recording error:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const processVoiceRecording = async (audioBlob) => {
    setVoiceLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');

      const data = await apiPost('/api/voice/transcribe', formData);

      if (data?.success) {
        setExtractedData({
          source: 'voice',
          ...data.extracted_data,
          raw_transcription: data.raw_transcription
        });
        setShowConfirmation(true);
        toast.success('Voice transcribed successfully! Review and confirm the details.');
      } else {
        toast.error('Voice transcription failed');
      }
    } catch (error) {
      const msg = error instanceof ApiError ? (error.message || 'Voice transcription failed') : 'Connection error. Please try again.';
      toast.error(msg);
      console.error('Voice processing error:', error);
    } finally {
      setVoiceLoading(false);
    }
  };

  // Image Analysis Functions
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WebP image');
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      toast.error('Image too large. Maximum 20 MB allowed.');
      return;
    }

    setImageLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const data = await apiPost('/api/image/analyze', formData);

      if (data?.success) {
        setExtractedData({
          source: 'image',
          ...data.extracted_data
        });
        setShowConfirmation(true);
        toast.success('Image analyzed! Review and confirm the extracted details.');
      } else {
        toast.error('Image analysis failed');
      }
    } catch (error) {
      const msg = error instanceof ApiError ? (error.message || 'Image analysis failed') : 'Connection error. Please try again.';
      toast.error(msg);
      console.error('Image analysis error:', error);
    } finally {
      setImageLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Apply extracted data to form
  const applyExtractedData = () => {
    if (!extractedData) return;

    const newForm = { ...productForm };
    
    if (extractedData.description) {
      newForm.description = extractedData.description;
    }
    if (extractedData.country_of_origin && COUNTRIES.includes(extractedData.country_of_origin)) {
      newForm.country_of_origin = extractedData.country_of_origin;
    }
    if (extractedData.hs_code) {
      newForm.current_hs_code = extractedData.hs_code;
    }

    setProductForm(newForm);
    setShowConfirmation(false);
    setExtractedData(null);
    toast.success('Data applied to form. You can edit before submitting.');
  };

  const dismissExtractedData = () => {
    setShowConfirmation(false);
    setExtractedData(null);
  };

  // ===================== AI ENHANCEMENT FUNCTIONS =====================
  
  // Calculate description quality score
  const getDescriptionQuality = (desc) => {
    if (!desc || desc.trim().length === 0) return { level: 'empty', color: 'slate', message: '' };
    
    const words = desc.trim().split(/\s+/).length;
    const hasNumbers = /\d/.test(desc);
    const hasMaterials = /(steel|plastic|metal|wood|glass|rubber|cotton|leather|aluminum|silicon|ceramic|fabric|polymer|alloy)/i.test(desc);
    const hasDimensions = /(inch|cm|mm|kg|lb|oz|gram|meter|foot|feet)/i.test(desc);
    
    let score = 0;
    if (words >= 5) score += 1;
    if (words >= 10) score += 1;
    if (words >= 15) score += 1;
    if (hasNumbers) score += 1;
    if (hasMaterials) score += 1;
    if (hasDimensions) score += 1;
    
    if (score >= 4) return { level: 'good', color: 'green', message: 'Detailed description - ready for accurate classification' };
    if (score >= 2) return { level: 'fair', color: 'amber', message: 'Add materials, dimensions, or technical specs for better accuracy' };
    return { level: 'poor', color: 'red', message: 'Too brief - add more details for accurate classification' };
  };

  // AI Enhance Description

  const handleEnhanceDescription = async () => {
    if (!productForm.description || productForm.description.trim().length < 3) {
      toast.error('Please enter a basic description first');
      return;
    }

    setEnhanceLoading(true);

    try {
      const data = await apiPost('/api/ai/enhance-description', { description: productForm.description });

      if (data?.enhanced_description) {
        setProductForm({ ...productForm, description: data.enhanced_description });
        toast.success('Description enhanced with technical details!');
      } else {
        toast.error('Enhancement failed');
      }
    } catch (error) {
      const msg = error instanceof ApiError ? (error.message || 'Enhancement failed') : 'Connection error';
      toast.error(msg);
    } finally {
      setEnhanceLoading(false);
    }
  };

  // AI HS Code Hint (debounced)
  const fetchHsCodeHint = async (description) => {
    if (!description || description.trim().length < 10) {
      setHsCodeHint(null);
      return;
    }

    setHsHintLoading(true);

    try {
      const data = await apiPost('/api/ai/hs-hint', { description });

      if (data?.suggested_hs_code) {
        setHsCodeHint({
          code: data.suggested_hs_code,
          description: data.code_description,
          confidence: data.confidence
        });
      } else {
        setHsCodeHint(null);
      }
    } catch {
      setHsCodeHint(null);
    } finally {
      setHsHintLoading(false);
    }
  };

  // Debounce HS hint fetch when description changes
  useEffect(() => {
    if (hsHintTimeoutRef.current) {
      clearTimeout(hsHintTimeoutRef.current);
    }
    
    if (productForm.description && productForm.description.trim().length >= 10) {
      hsHintTimeoutRef.current = setTimeout(() => {
        fetchHsCodeHint(productForm.description);
      }, 1500); // 1.5 second debounce
    } else {
      setHsCodeHint(null);
    }

    return () => {
      if (hsHintTimeoutRef.current) {
        clearTimeout(hsHintTimeoutRef.current);
      }
    };
  }, [productForm.description]);

  // Apply HS hint to form
  const applyHsHint = () => {
    if (hsCodeHint?.code) {
      setProductForm({ ...productForm, current_hs_code: hsCodeHint.code });
      toast.success('HS code applied');
    }
  };

  // ===================== BULK AI ENHANCEMENT FUNCTIONS =====================

  // Analyze file quality when preview is generated

  const analyzeFileQuality = (preview) => {
    if (!preview || !preview.rows || preview.rows.length === 0) {
      setFileQualityAnalysis(null);
      return;
    }

    // Find description column index (handle various naming conventions)
    const descIndex = preview.headers.findIndex(h => {
      const lower = h.toLowerCase().replace(/[_\s-]/g, '');
      return lower.includes('description') || lower.includes('product') || lower === 'desc';
    });
    
    // Find HS code column index (handle various naming conventions)
    const hsIndex = preview.headers.findIndex(h => {
      const lower = h.toLowerCase().replace(/[_\s-]/g, '');
      return lower.includes('hscode') || lower.includes('tariff') || lower.includes('htscode') || 
             (lower.includes('hs') && lower.includes('code')) || lower === 'hs';
    });

    if (descIndex === -1) {
      console.warn('No description column found in headers:', preview.headers);
      setFileQualityAnalysis(null);
      return;
    }

    let good = 0, fair = 0, poor = 0, missingHs = 0;
    const rowDetails = [];

    preview.rows.forEach((row, idx) => {
      const desc = row[descIndex] || '';
      const hsCode = hsIndex >= 0 ? (row[hsIndex] || '') : '';
      const quality = getDescriptionQuality(desc);
      
      if (quality.level === 'good') good++;
      else if (quality.level === 'fair') fair++;
      else if (quality.level !== 'empty') poor++;
      
      if (!hsCode.toString().trim()) missingHs++;
      
      rowDetails.push({ 
        index: idx, 
        description: desc, 
        quality: quality.level,
        hasHsCode: !!hsCode.toString().trim()
      });
    });

    setFileQualityAnalysis({
      totalRows: preview.rows.length,
      good,
      fair,
      poor,
      missingHs,
      descriptionIndex: descIndex,
      hsCodeIndex: hsIndex,
      rowDetails
    });
  };

  // Bulk enhance descriptions

  const handleBulkEnhance = async () => {
    if (!filePreview || !fileQualityAnalysis) return;
    
    const rowsToEnhance = fileQualityAnalysis.rowDetails
      .filter(r => r.quality !== 'good' && r.description.trim().length >= 3)
      .slice(0, 20); // Limit to 20 rows at a time
    
    if (rowsToEnhance.length === 0) {
      toast.info('No descriptions need enhancement');
      return;
    }

    setBulkEnhanceLoading(true);

    try {
      const data = await apiPost('/api/ai/bulk-enhance', {
        descriptions: rowsToEnhance.map(r => ({
          index: r.index,
          description: r.description
        }))
      });

      if (data?.enhanced) {
        // Update file preview with enhanced descriptions
        const newRows = [...filePreview.rows];
        data.enhanced.forEach(item => {
          if (newRows[item.index]) {
            newRows[item.index][fileQualityAnalysis.descriptionIndex] = item.enhanced_description;
          }
        });

        const newPreview = { ...filePreview, rows: newRows };
        setFilePreview(newPreview);
        analyzeFileQuality(newPreview);

        toast.success(`Enhanced ${data.enhanced.length} descriptions!`);
      } else {
        toast.error('Enhancement failed');
      }
    } catch (error) {
      const msg = error instanceof ApiError ? (error.message || 'Enhancement failed') : 'Connection error';
      toast.error(msg);
    } finally {
      setBulkEnhanceLoading(false);
    }
  };

  // Bulk suggest HS codes
  const handleBulkHsHint = async () => {
    if (!filePreview || !fileQualityAnalysis) return;
    
    const rowsNeedingHs = fileQualityAnalysis.rowDetails
      .filter(r => !r.hasHsCode && r.description.trim().length >= 10)
      .slice(0, 15); // Limit to 15 rows at a time
    
    if (rowsNeedingHs.length === 0) {
      toast.info('All rows already have HS codes');
      return;
    }

    setBulkHsHintLoading(true);

    try {
      const data = await apiPost('/api/ai/bulk-hs-hint', {
        items: rowsNeedingHs.map(r => ({
          index: r.index,
          description: r.description
        }))
      });

      if (data?.suggestions) {
        // Update file preview with suggested HS codes
        const newRows = [...filePreview.rows];
        let hsIndex = fileQualityAnalysis.hsCodeIndex;

        // If no HS code column exists, we can't add it in preview
        // Just show the suggestions count
        if (hsIndex === -1) {
          toast.success(`Found ${data.suggestions.length} HS code suggestions. They will be applied during analysis.`);
        } else {
          data.suggestions.forEach(item => {
            if (newRows[item.index] && item.suggested_hs_code) {
              newRows[item.index][hsIndex] = item.suggested_hs_code;
            }
          });

          const newPreview = { ...filePreview, rows: newRows };
          setFilePreview(newPreview);
          analyzeFileQuality(newPreview);

          toast.success(`Suggested HS codes for ${data.suggestions.length} rows!`);
        }
      } else {
        toast.error('HS code suggestion failed');
      }
    } catch (error) {
      const msg = error instanceof ApiError ? (error.message || 'HS code suggestion failed') : 'Connection error';
      toast.error(msg);
    } finally {
      setBulkHsHintLoading(false);
    }
  };

  // ===================== END AI ENHANCEMENT FUNCTIONS =====================

  // Handle single product analysis

  const handleSingleAnalysis = async (e) => {
    e.preventDefault();
    
    if (!productForm.description || !productForm.country_of_origin || !productForm.import_country) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSingleLoading(true);

    try {
      const data = await apiPost('/api/analyze/single', {
        description: productForm.description,
        country_of_origin: productForm.country_of_origin,
        import_country: productForm.import_country,
        current_hs_code: productForm.current_hs_code || null,
        unit_cost: productForm.unit_cost ? parseFloat(productForm.unit_cost) : null,
        annual_volume: productForm.annual_volume ? parseInt(productForm.annual_volume) : null,
      });
      toast.success('Analysis complete!');
      navigate(`/analysis/${data.analysis_id}`);
    } catch (error) {
      const msg = error instanceof ApiError ? (error.message || 'Analysis failed') : 'Connection error. Please try again.';
      toast.error(msg);
    } finally {
      setSingleLoading(false);
    }
  };

  // AI Enhance Sourcing Description

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  // Handle file drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  }, []);

  // Handle file input change
  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  // Process uploaded file
  const processFile = (file) => {
    const validTypes = ['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
    const validExtensions = ['.csv', '.xlsx', '.xls'];
    
    const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
    
    if (!validTypes.includes(file.type) && !hasValidExtension) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }

    setUploadedFile(file);
    
    const reader = new FileReader();
    
    // Preview for CSV files
    if (file.name.endsWith('.csv')) {
      reader.onload = (e) => {
        const text = e.target.result;
        const allLines = text.split('\n').filter(line => line.trim());
        const headers = allLines[0].split(',');
        const allRows = allLines.slice(1).map(line => line.split(','));
        const previewRows = allRows.slice(0, 5); // Preview first 5
        
        const preview = { headers, rows: allRows };
        setFilePreview({ headers, rows: previewRows }); // Show preview
        analyzeFileQuality(preview); // Analyze all rows
      };
      reader.readAsText(file);
    } else {
      // Parse Excel file
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
          
          if (jsonData.length > 0) {
            const headers = jsonData[0].map(h => String(h || '').trim());
            const allRows = jsonData.slice(1).map(row => 
              headers.map((_, i) => String(row[i] || '').trim())
            );
            const previewRows = allRows.slice(0, 5);
            
            const preview = { headers, rows: allRows };
            setFilePreview({ headers, rows: previewRows });
            analyzeFileQuality(preview);
          } else {
            setFilePreview({ headers: ['Empty file'], rows: [] });
            setFileQualityAnalysis(null);
          }
        } catch (err) {
          console.error('Excel parsing error:', err);
          setFilePreview({ headers: ['Error parsing Excel file'], rows: [] });
          setFileQualityAnalysis(null);
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  // Handle bulk upload
  const handleBulkUpload = async () => {
    if (!uploadedFile) {
      toast.error('Please select a file first');
      return;
    }

    setBulkLoading(true);

    const formData = new FormData();
    formData.append('file', uploadedFile);

    try {
      const data = await apiPost('/api/analyze/bulk', formData);
      toast.success(`Analysis complete! ${data.total_skus} SKUs processed.`);
      navigate(`/analysis/${data.analysis_id}`);
    } catch (error) {
      const msg = error instanceof ApiError ? (error.message || 'Upload failed') : 'Connection error. Please try again.';
      toast.error(msg);
    } finally {
      setBulkLoading(false);
    }
  };

  const clearFile = () => {
    setUploadedFile(null);
    setFilePreview(null);
    setFileQualityAnalysis(null);
  };

  // ERP Smart Import handlers

  return (
          <>
          {/* Main Content - Split View */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Single Product Analyzer */}
            <div className="card-modern p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#2563EB] to-[#60A5FA] flex items-center justify-center shadow-lg">
                    <Search className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h2 className="font-outfit text-xl font-semibold text-slate-900">
                      Single Product
                    </h2>
                    <p className="text-sm text-slate-500 font-jakarta">Analyze one product at a time</p>
                  </div>
                </div>
                
                {/* Voice & Camera Buttons */}
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={voiceLoading || imageLoading}
                    className={`rounded-xl transition-all ${isRecording ? 'bg-red-50 border-red-300 text-red-600 animate-pulse' : 'hover:bg-blue-50 hover:border-blue-300'}`}
                    data-testid="voice-input-btn"
                    title={isRecording ? "Stop recording" : "Speak product description"}
                  >
                    {voiceLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : isRecording ? (
                      <MicOff className="w-5 h-5" />
                    ) : (
                      <Mic className="w-5 h-5" />
                    )}
                  </Button>
                  
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={voiceLoading || imageLoading}
                    className="rounded-xl hover:bg-blue-50 hover:border-blue-300"
                    data-testid="camera-input-btn"
                    title="Upload product photo"
                  >
                    {imageLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Camera className="w-5 h-5" />
                    )}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="image-file-input"
                  />
                </div>
              </div>

              {/* Voice/Image Extraction Confirmation Panel */}
              {showConfirmation && extractedData && (
                <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl animate-in slide-in-from-top">
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${extractedData.source === 'voice' ? 'bg-purple-100' : 'bg-green-100'}`}>
                      {extractedData.source === 'voice' ? (
                        <Mic className="w-4 h-4 text-purple-600" />
                      ) : (
                        <ImageIcon className="w-4 h-4 text-green-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-800 text-sm">
                        {extractedData.source === 'voice' ? 'Voice Transcription' : 'Image Analysis'} Complete
                      </p>
                      <p className="text-xs text-slate-500">Review extracted data before applying</p>
                    </div>
                    <button onClick={dismissExtractedData} className="text-slate-400 hover:text-slate-600">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    {extractedData.raw_transcription && (
                      <div className="bg-white/60 p-2 rounded-lg">
                        <p className="text-xs text-slate-500 mb-1">Raw transcription:</p>
                        <p className="text-slate-700 italic">"{extractedData.raw_transcription}"</p>
                      </div>
                    )}
                    
                    {extractedData.description && (
                      <div className="flex items-start gap-2">
                        <span className="text-slate-500 min-w-[80px]">Description:</span>
                        <span className="text-slate-800">{extractedData.description}</span>
                      </div>
                    )}
                    
                    {extractedData.materials?.length > 0 && (
                      <div className="flex items-start gap-2">
                        <span className="text-slate-500 min-w-[80px]">Materials:</span>
                        <div className="flex flex-wrap gap-1">
                          {extractedData.materials.map((m, i) => (
                            <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">{m}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {extractedData.country_of_origin && (
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500 min-w-[80px]">Origin:</span>
                        <span className="text-slate-800">{extractedData.country_of_origin}</span>
                      </div>
                    )}
                    
                    {extractedData.hs_code && (
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500 min-w-[80px]">{currentMarket.codeLabel}:</span>
                        <span className="font-mono text-slate-800">{extractedData.hs_code}</span>
                      </div>
                    )}
                    
                    {extractedData.confidence_score && (
                      <div className="flex items-center gap-2">
                        <span className="text-slate-500 min-w-[80px]">Confidence:</span>
                        <span className="text-slate-800">{Math.round(extractedData.confidence_score * 100)}%</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2 mt-4">
                    <Button
                      type="button"
                      onClick={applyExtractedData}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                      size="sm"
                      data-testid="apply-extracted-btn"
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Apply to Form
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={dismissExtractedData}
                      size="sm"
                      data-testid="dismiss-extracted-btn"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Dismiss
                    </Button>
                  </div>
                </div>
              )}

              <form onSubmit={handleSingleAnalysis} className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-700 font-jakarta font-medium">Product Description *</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleEnhanceDescription}
                      disabled={enhanceLoading || !productForm.description || productForm.description.trim().length < 3}
                      className="text-xs h-7 px-2 text-[#6366F1] hover:text-[#4F46E5] hover:bg-[#6366F1]/10"
                      data-testid="ai-enhance-btn"
                    >
                      {enhanceLoading ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <Sparkles className="w-3 h-3 mr-1" />
                      )}
                      AI Enhance
                    </Button>
                  </div>
                  <Textarea
                    placeholder="Enter detailed product description or use voice/camera..."
                    value={productForm.description}
                    onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                    className="min-h-[100px] input-modern resize-none"
                    required
                    data-testid="product-description-input"
                  />
                  {/* Description Quality Indicator */}
                  {productForm.description && (
                    <div className={`flex items-center gap-2 text-xs ${
                      getDescriptionQuality(productForm.description).color === 'green' ? 'text-green-600' :
                      getDescriptionQuality(productForm.description).color === 'amber' ? 'text-amber-600' :
                      'text-red-500'
                    }`}>
                      <div className={`w-2 h-2 rounded-full ${
                        getDescriptionQuality(productForm.description).color === 'green' ? 'bg-green-500' :
                        getDescriptionQuality(productForm.description).color === 'amber' ? 'bg-amber-500' :
                        'bg-red-500'
                      }`} />
                      {getDescriptionQuality(productForm.description).message}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-jakarta font-medium">Country of Origin *</Label>
                    <Select
                      value={productForm.country_of_origin}
                      onValueChange={(value) => setProductForm({ ...productForm, country_of_origin: value })}
                    >
                      <SelectTrigger className="input-modern" data-testid="origin-country-select">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map((country) => (
                          <SelectItem key={country} value={country}>{country}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-700 font-jakarta font-medium">Import Market *</Label>
                    <Select
                      value={productForm.import_country}
                      onValueChange={(value) => setProductForm({ ...productForm, import_country: value })}
                    >
                      <SelectTrigger className="input-modern" data-testid="import-country-select">
                        <SelectValue placeholder="Select import market" />
                      </SelectTrigger>
                      <SelectContent>
                        <div className="px-2 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Markets</div>
                        <SelectItem value="United States">United States (HTS)</SelectItem>
                        <SelectItem value="European Union">European Union (CN/TARIC)</SelectItem>
                        <SelectItem value="United Kingdom">United Kingdom (UK Tariff)</SelectItem>
                        <div className="border-t border-slate-100 my-1" />
                        <div className="px-2 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">All Countries</div>
                        {COUNTRIES.filter(c => !['United States', 'United Kingdom'].includes(c)).map((country) => (
                          <SelectItem key={country} value={country}>{country}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {currentMarket.market !== 'US' && (
                      <p className="text-xs text-blue-600 mt-1">
                        {currentMarket.market === 'EU' ? 'Using EU Combined Nomenclature (CN) codes' : 
                         currentMarket.market === 'UK' ? 'Using UK Global Tariff commodity codes' : 
                         'Using international HS codes'}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-700 font-jakarta font-medium">Current {currentMarket.codeLabel} (optional)</Label>
                  <Input
                    placeholder={currentMarket.market === 'EU' ? 'e.g., 8471.30.00' : currentMarket.market === 'UK' ? 'e.g., 8471.30.0000' : 'e.g., 8471.30.0000'}
                    value={productForm.current_hs_code}
                    onChange={(e) => setProductForm({ ...productForm, current_hs_code: e.target.value })}
                    className="input-modern"
                    data-testid="hs-code-input"
                  />
                  {/* AI HS Code Hint */}
                  {hsHintLoading && (
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      AI analyzing...
                    </div>
                  )}
                  {hsCodeHint && !hsHintLoading && (
                    <div className="flex items-center gap-2 p-2 bg-[#6366F1]/5 border border-[#6366F1]/20 rounded-lg">
                      <Sparkles className="w-4 h-4 text-[#6366F1] flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="text-xs text-slate-600">AI suggests: </span>
                        <span className="text-xs font-medium text-[#6366F1]">{hsCodeHint.code}</span>
                        {hsCodeHint.description && (
                          <span className="text-xs text-slate-500 ml-1">({hsCodeHint.description})</span>
                        )}
                      </div>
                      {productForm.current_hs_code === hsCodeHint.code ? (
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <Check className="w-3 h-3" /> Match
                        </span>
                      ) : (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={applyHsHint}
                          className="h-6 px-2 text-xs text-[#6366F1] hover:bg-[#6366F1]/10"
                        >
                          Apply
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-jakarta font-medium">
                      Unit Cost ({getUnitCostCurrency().code}) (optional)
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                        {getUnitCostCurrency().symbol}
                      </span>
                      <Input
                        type="number"
                        placeholder="0.00"
                        value={productForm.unit_cost}
                        onChange={(e) => setProductForm({ ...productForm, unit_cost: e.target.value })}
                        className="input-modern pl-8"
                        data-testid="unit-cost-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-700 font-jakarta font-medium">Annual Volume (optional)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 10000"
                      value={productForm.annual_volume}
                      onChange={(e) => setProductForm({ ...productForm, annual_volume: e.target.value })}
                      className="input-modern"
                      data-testid="annual-volume-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full btn-primary-modern py-6"
                  disabled={singleLoading}
                  data-testid="analyze-single-btn"
                >
                  {singleLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      Analyze Product
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Bulk Catalog Analyzer */}
            <div className="card-modern p-6" ref={bulkSectionRef}>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#06B6D4] to-[#2DD4BF] flex items-center justify-center shadow-lg">
                  <Upload className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <h2 className="font-outfit text-xl font-semibold text-slate-900">
                    Bulk Catalog
                  </h2>
                  <p className="text-sm text-slate-500 font-jakarta">Analyze up to {user?.sku_limit || 200} SKUs</p>
                </div>
              </div>
              
              {/* AI Enhancement Banner */}
              <div className="flex items-center gap-2 px-3 py-2 mb-4 bg-gradient-to-r from-[#6366F1]/10 to-[#8B5CF6]/10 rounded-xl border border-[#6366F1]/20">
                <Sparkles className="w-4 h-4 text-[#6366F1]" />
                <span className="text-xs text-[#6366F1] font-medium">AI-Powered</span>
                <span className="text-xs text-slate-600">• AI improvement tools available on classification results</span>
              </div>

              {/* Drop Zone */}
              <div
                className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-300 ${
                  dragActive 
                    ? 'border-[#2563EB] bg-[#2563EB]/5' 
                    : 'border-slate-200 hover:border-[#2563EB]/50 hover:bg-slate-50'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                data-testid="file-dropzone"
              >
                {uploadedFile ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#10B981] to-[#2DD4BF] flex items-center justify-center">
                        <FileSpreadsheet className="w-6 h-6 text-white" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-slate-900 font-jakarta">{uploadedFile.name}</p>
                        <p className="text-sm text-slate-500 font-jakarta">
                          {(uploadedFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <button 
                        onClick={clearFile}
                        className="ml-4 p-2 hover:bg-slate-100 rounded-xl transition-colors"
                        data-testid="clear-file-btn"
                      >
                        <X className="w-5 h-5 text-slate-400" />
                      </button>
                    </div>

                    {/* File Preview */}
                    {filePreview && filePreview.rows.length > 0 && (
                      <div className="mt-4 bg-slate-50 rounded-xl p-4 max-h-[180px] overflow-auto text-left">
                        <p className="text-xs text-slate-500 mb-2 font-jakarta">Preview (first 5 rows):</p>
                        <table className="w-full text-xs">
                          <thead>
                            <tr>
                              {filePreview.headers.slice(0, 4).map((header, i) => (
                                <th key={i} className="text-left p-1 text-slate-600 font-medium font-jakarta">
                                  {header.trim().substring(0, 15)}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {filePreview.rows.slice(0, 4).map((row, i) => (
                              <tr key={i} className="border-t border-slate-200">
                                {row.slice(0, 4).map((cell, j) => (
                                  <td key={j} className="p-1 text-slate-500 font-jakarta">
                                    {cell.trim().substring(0, 15)}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* File Quality Summary */}
                    {fileQualityAnalysis && (
                      <div className="mt-4 bg-gradient-to-r from-[#6366F1]/5 to-[#8B5CF6]/5 rounded-xl p-4 border border-[#6366F1]/20">
                        <div className="flex items-center gap-2 mb-3">
                          <Sparkles className="w-4 h-4 text-[#6366F1]" />
                          <span className="text-sm font-medium text-slate-700">AI File Analysis</span>
                        </div>
                        
                        <div className="grid grid-cols-4 gap-2 mb-3">
                          <div className="text-center p-2 bg-white rounded-lg">
                            <p className="text-lg font-bold text-slate-900">{fileQualityAnalysis.totalRows}</p>
                            <p className="text-xs text-slate-500">Total Rows</p>
                          </div>
                          <div className="text-center p-2 bg-green-50 rounded-lg">
                            <p className="text-lg font-bold text-green-600">{fileQualityAnalysis.good}</p>
                            <p className="text-xs text-green-600">Good</p>
                          </div>
                          <div className="text-center p-2 bg-amber-50 rounded-lg">
                            <p className="text-lg font-bold text-amber-600">{fileQualityAnalysis.fair}</p>
                            <p className="text-xs text-amber-600">Fair</p>
                          </div>
                          <div className="text-center p-2 bg-red-50 rounded-lg">
                            <p className="text-lg font-bold text-red-500">{fileQualityAnalysis.poor}</p>
                            <p className="text-xs text-red-500">Brief</p>
                          </div>
                        </div>

                        {(fileQualityAnalysis.fair + fileQualityAnalysis.poor) > 0 && (
                          <p className="text-xs text-slate-500">
                            {fileQualityAnalysis.fair + fileQualityAnalysis.poor} descriptions could benefit from more detail. AI improvement tools will be available on the results page after analysis.
                          </p>
                        )}
                        {fileQualityAnalysis.missingHs > 0 && (
                          <p className="text-xs text-slate-500 mt-1">
                            {fileQualityAnalysis.missingHs} rows missing HS codes — AI suggestions available after classification.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center mx-auto mb-4">
                      <Upload className="w-8 h-8 text-slate-400" />
                    </div>
                    <p className="text-slate-600 mb-2 font-jakarta">
                      Drag and drop your file here, or{' '}
                      <label className="text-[#2563EB] font-semibold cursor-pointer hover:underline">
                        browse
                        <input
                          type="file"
                          className="hidden"
                          accept=".csv,.xlsx,.xls"
                          onChange={handleFileChange}
                          data-testid="file-input"
                        />
                      </label>
                    </p>
                    <p className="text-sm text-slate-400 font-jakarta">
                      Supported: CSV, XLSX
                    </p>
                  </>
                )}
              </div>

              {/* Required Columns Info */}
              <div className="mt-4 bg-gradient-to-r from-[#2563EB]/5 to-[#06B6D4]/5 rounded-xl p-4 border border-[#2563EB]/10">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-[#2563EB] flex-shrink-0 mt-0.5" />
                  <div className="text-sm font-jakarta">
                    <p className="font-medium text-slate-700 mb-1">Required columns:</p>
                    <ul className="text-slate-500 space-y-0.5">
                      <li>• description (or product_description)</li>
                      <li>• country_of_origin</li>
                      <li>• import_country</li>
                    </ul>
                    <p className="text-slate-400 mt-2 text-xs">
                      Optional: current_hs_code, unit_cost, annual_volume
                    </p>
                  </div>
                </div>
              </div>

              {/* Upload Button */}
              <Button
                onClick={handleBulkUpload}
                className="w-full mt-6 btn-primary-modern py-6"
                disabled={!uploadedFile || bulkLoading}
                data-testid="upload-analyze-btn"
              >
                {bulkLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    Upload & Analyze
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>

              {/* SKU Limit Warning */}
              {user?.subscription_tier === 'starter' && (
                <p className="mt-4 text-center text-sm text-slate-500 font-jakarta">
                  Free tier: {user?.sku_limit || 200} SKUs.{' '}
                  <a href="/billing" className="text-[#2563EB] font-semibold hover:underline">
                    Upgrade for more
                  </a>
                </p>
              )}
            </div>
          </div>

          </>
  );
}
